import * as R from 'ramda';
// helpers
import * as G from '../../helpers';
import * as GC from '../../constants';
// components
import { transformSearchCriteriaBeforeReportPost } from '../../components/edit-report/helpers';
// centralized-dispatch
import * as C from './constants';
//////////////////////////////////////////////////

export const getInitialUISettings = () => {
  const savedSettings = G.getItemFromLocalStorage(C.CENTRALIZED_DISPATCH_UI_SETTINGS) || {};
  const searchMode = R.propOr('drivers', 'searchMode', savedSettings);
  const legacySettings = R.pick(R.keys(C.defaultModeUISettings), savedSettings);

  return {
    trips: {
      ...C.defaultModeUISettings,
      ...R.propOr({}, 'trips', savedSettings),
    },
    drivers: {
      ...C.defaultModeUISettings,
      ...R.propOr({}, 'drivers', savedSettings),
    },
    searchMode,
    [searchMode]: {
      ...C.defaultModeUISettings,
      ...legacySettings,
      ...R.propOr({}, searchMode, savedSettings),
    },
    filtersExpanded: R.propOr(false, 'filtersExpanded', savedSettings),
  };
};

export const getDefaultFilterValues = () => {
  const currentDate = G.getCurrentDate();

  return {
    [C.MODE]: null,
    [C.EVENTS_MAX]: '',
    [C.EVENTS_MIN]: '',
    [GC.FIELD_ORIGIN_ZONE]: null,
    [GC.FIELD_ORIGIN_RADIUS]: '',
    [C.DESTINATION_DATE_TO]: null,
    [GC.FIELD_ORIGIN_LOCATION]: '',
    [C.DESTINATION_DATE_FROM]: null,
    [C.DRIVER_ENTERPRISE_GUID]: null,
    [GC.FIELD_ORIGIN_RADIUS_UOM]: '',
    [GC.FIELD_DESTINATION_ZONE]: null,
    [GC.FIELD_DESTINATION_RADIUS]: '',
    [C.ORIGIN_DATE_FROM]: currentDate,
    [GC.FIELD_DESTINATION_LOCATION]: '',
    [C.SEARCH_BY]: C.LAST_KNOWN_LOCATION,
    [GC.FIELD_DESTINATION_RADIUS_UOM]: '',
    [GC.FIELD_BRANCH]: { [GC.FIELD_GUID]: null },
    [C.ORIGIN_DATE_TO]: G.addDateTime(currentDate, 3, 'days'),
    [C.ADDITIONAL_FILTERS_AND_SORT]: C.defaultAdditionalFiltersAndSort,
  };
};

const relativeDateFields = [
  C.ORIGIN_DATE_TO,
  C.DESTINATION_DATE_TO,
  C.DESTINATION_DATE_FROM,
];

const rebaseFilterDates = (filterValues) => {
  const savedOriginDate = R.prop(C.ORIGIN_DATE_FROM, filterValues);
  const currentDate = G.getCurrentDate();
  const valuesWithCurrentOriginDate = R.assoc(
    C.ORIGIN_DATE_FROM,
    currentDate,
    filterValues,
  );

  if (R.not(G.isValidDate(savedOriginDate))) return valuesWithCurrentOriginDate;

  return R.reduce((values, fieldName) => {
    const savedDate = R.prop(fieldName, filterValues);

    if (R.not(G.isValidDate(savedDate))) return values;

    const interval = G.getDateRange(savedOriginDate, savedDate, 'd');

    return R.assoc(
      fieldName,
      G.addDateTime(currentDate, interval, 'days'),
      values,
    );
  }, valuesWithCurrentOriginDate, relativeDateFields);
};

const getFormValuesCriterion = (report) => R.find(
  R.propEq(C.FORM_VALUES_SEARCH_PROPERTY, GC.FIELD_PROPERTY_NAME),
  R.pathOr([], ['searchCriteria'], report),
);

const parseReportFormValues = (formValuesCriterion) => {
  const stringValue = R.propOr('{}', 'stringValue', formValuesCriterion);

  try {
    return JSON.parse(stringValue);
  } catch (error) {
    return {};
  }
};

export const getFilterValues = (report = {}) => {
  const formValuesCriterion = getFormValuesCriterion(report);

  const formValues = G.isNilOrEmpty(formValuesCriterion)
    ? getDefaultFilterValues()
    : parseReportFormValues(formValuesCriterion);

  const filters = R.reject(
    R.propEq(C.FORM_VALUES_SEARCH_PROPERTY, GC.FIELD_PROPERTY_NAME),
    R.pathOr([], ['searchCriteria'], report),
  );

  const sort = R.pathOr([], ['orderFields'], report);

  return rebaseFilterDates({
    ...formValues,
    [C.ADDITIONAL_FILTERS_AND_SORT]: { filters, sort },
  });
};

export const getReportOptions = (reports) => R.map(
  (report) => ({
    value: report,
    label: R.propOr(false, GC.FIELD_REPORT_DEFAULT, report)
      ? `${R.prop(GC.FIELD_NAME, report)} (default)`
      : R.prop(GC.FIELD_NAME, report),
  }),
  reports,
);

const isAvailableDriverField = R.compose(
  R.startsWith(C.AVAILABLE_DRIVER_FIELD_PREFIX),
  R.propOr('', GC.FIELD_NAME),
);

const removeAvailableDriverFieldPrefix = R.replace(
  new RegExp(`^${C.AVAILABLE_DRIVER_FIELD_PREFIX}`),
  '',
);

export const getCentralizedDispatchReportFieldNames = (report, availableDrivers = false) => R.compose(
  R.map((field) => R.when(
    () => availableDrivers,
    removeAvailableDriverFieldPrefix,
    R.prop(GC.FIELD_NAME, field),
  )),
  R.filter(availableDrivers ? isAvailableDriverField : R.complement(isAvailableDriverField)),
  R.sortBy(R.prop('sequence')),
  R.pathOr([], ['fields']),
)(report);

export const getCentralizedDispatchTableReport = (report, availableDrivers = false) => {
  const fields = R.compose(
    G.mapIndexed((name, index) => ({ name, sequence: R.inc(index) })),
    R.map((field) => R.when(
      () => availableDrivers,
      removeAvailableDriverFieldPrefix,
      R.prop(GC.FIELD_NAME, field),
    )),
    R.filter(availableDrivers ? isAvailableDriverField : R.complement(isAvailableDriverField)),
    R.sortBy(R.prop('sequence')),
    R.pathOr([], ['fields']),
  )(report);

  return R.assoc('fields', fields, report);
};

const getMergedReportFields = (report, dispatchBoardFields, availableDriverFields) => {
  const currentFields = R.indexBy(
    R.prop(GC.FIELD_NAME),
    R.pathOr([], ['fields'], report),
  );
  const fieldNames = R.concat(
    dispatchBoardFields,
    R.map(R.concat(C.AVAILABLE_DRIVER_FIELD_PREFIX), availableDriverFields),
  );

  return G.mapIndexed((name, index) => ({
    ...R.propOr({}, name, currentFields),
    name,
    sequence: R.inc(index),
  }), fieldNames);
};

export const setCentralizedDispatchReportFields = (
  report,
  dispatchBoardFields,
  availableDriverFields,
) => R.assoc(
  'fields',
  getMergedReportFields(report, dispatchBoardFields, availableDriverFields),
  report,
);

const prepareReportChildItems = (items, isCreate) => G.mapIndexed((item, index) => ({
  ...item,
  [GC.FIELD_GUID]: G.ifElse(isCreate, null, R.prop(GC.FIELD_GUID, item)),
  sequence: R.inc(index),
}), items);

export const buildCentralizedDispatchReport = ({
  report,
  isCreate,
  formValues,
  reportValues,
  dispatchBoardFields,
  availableDriverFields,
}) => {
  const additionalFiltersAndSort = R.pathOr(
    C.defaultAdditionalFiltersAndSort,
    [C.ADDITIONAL_FILTERS_AND_SORT],
    formValues,
  );

  const serializedFormValues = JSON.stringify(R.omit([C.ADDITIONAL_FILTERS_AND_SORT], formValues));

  const formValuesCriterion = {
    dataType: 'string',
    stringValue: serializedFormValues,
    [GC.FIELD_PROPERTY_NAME]: C.FORM_VALUES_SEARCH_PROPERTY,
  };

  const searchCriteria = R.append(
    formValuesCriterion,
    prepareReportChildItems(R.propOr([], 'filters', additionalFiltersAndSort), isCreate),
  );

  const orderFields = prepareReportChildItems(
    R.propOr([], 'sort', additionalFiltersAndSort),
    isCreate,
  );

  const reportWithFields = setCentralizedDispatchReportFields(
    report,
    dispatchBoardFields,
    availableDriverFields,
  );

  const nextReport = {
    ...reportWithFields,
    ...reportValues,
    orderFields,
    searchCriteria,
    [GC.FIELD_TYPE]: GC.CENTRALIZED_DISPATCH_REPORT,
  };

  if (R.not(isCreate)) return nextReport;

  return {
    ...nextReport,
    version: null,
    [GC.FIELD_GUID]: null,
    fields: prepareReportChildItems(R.propOr([], 'fields', nextReport), true),
  };
};

export const getEmptyCentralizedDispatchReport = () => ({
  fields: [],
  orderFields: [],
  searchCriteria: [],
  [GC.FIELD_TYPE]: GC.CENTRALIZED_DISPATCH_REPORT,
});

const getDateRangeSearchCriteria = (dateFrom, dateTo, propertyName) => {
  if (G.isAllNilOrEmpty([dateFrom, dateTo])) return [];

  return [{
    propertyName,
    dataType: 'date',
    operation: GC.FIELD_DATE_RANGE,
    to: G.toEndOfDayFromDate(dateTo),
    from: G.toStartOfDayFromDate(dateFrom),
  }];
};

const getNumberRangeSearchCriteria = (from, to, propertyName) => {
  if (G.isAllNilOrEmpty([from, to])) return [];

  return [{
    propertyName,
    dataType: 'number',
    operation: 'range',
    numberRange: {
      from: G.toNumber(from),
      to: G.isNilOrEmpty(to) ? 100 : G.toNumber(to),
    },
  }];
};

const countryCodeByName = {
  Canada: GC.ENUM_CA,
  Mexico: GC.ENUM_MX,
  'United States': GC.ENUM_US,
};

const getLocationStateSearchValue = (location) => {
  const state = R.prop(GC.FIELD_STATE, location);

  if (
    G.notEquals(R.length(state), 2)
    || G.isNilOrEmpty(R.prop(GC.FIELD_COUNTRY, location))
  ) return state;

  const countryCode = R.prop(R.prop(GC.FIELD_COUNTRY, location), countryCodeByName);

  return R.compose(
    R.propOr(state, 'labelFull'),
    R.find(R.propEq(R.toUpper(state), 'abbreviation')),
    R.propOr([], countryCode),
  )(GC.STATE_OPTIONS_MAP);
};

const getOriginLocationSearchCriteria = (originLocation) => {
  if (G.isNilOrEmpty(originLocation)) return [];

  const locationFields = [
    {
      value: R.prop(GC.FIELD_CITY, originLocation),
      propertyName: GC.GRC.FIRST_EVENT_LOCATION_CITY,
    },
    {
      propertyName: GC.GRC.FIRST_EVENT_LOCATION_STATE,
      value: getLocationStateSearchValue(originLocation),
    },
  ];

  return R.compose(
    R.map(({ value, propertyName }) => (
      R.prop('filter', G.createReportFilterFromTitleSearch({
        value,
        fieldProps: {
          type: 'string',
          value: propertyName,
        },
      }))
    )),
    R.reject(({ value }) => G.isNilOrEmpty(value)),
  )(locationFields);
};

const getDestinationLocationSearchCriteria = (destinationLocation) => {
  if (G.isNilOrEmpty(destinationLocation)) return [];

  const locationFields = [
    {
      propertyName: GC.GRC.LAST_EVENT_LOCATION_CITY,
      value: R.prop(GC.FIELD_CITY, destinationLocation),
    },
    {
      propertyName: GC.GRC.LAST_EVENT_LOCATION_STATE,
      value: getLocationStateSearchValue(destinationLocation),
    },
  ];

  return R.compose(
    R.map(({ value, propertyName }) => (
      R.prop('filter', G.createReportFilterFromTitleSearch({
        value,
        fieldProps: {
          type: 'string',
          value: propertyName,
        },
      }))
    )),
    R.reject(({ value }) => G.isNilOrEmpty(value)),
  )(locationFields);
};

const getLocationRadiusSearchCriteria = (
  location,
  radius,
  radiusUom,
  propertyName,
) => {
  if (G.isAnyNilOrEmpty([location, radius, radiusUom])) return [];

  return [{
    propertyName,
    dataType: 'radius',
    operation: 'within',
    radiusValue: {
      radius,
      radiusUom,
      latitude: R.prop(GC.FIELD_LATITUDE, location),
      longitude: R.prop(GC.FIELD_LONGITUDE, location),
    },
  }];
};

const getDriverLocationSearchCriteria = (
  location,
  cityPropertyName,
  statePropertyName,
) => {
  if (G.isNilOrEmpty(location)) return [];

  const locationFields = [
    {
      propertyName: cityPropertyName,
      value: R.prop(GC.FIELD_CITY, location),
    },
    {
      propertyName: statePropertyName,
      value: getLocationStateSearchValue(location),
    },
  ];

  return R.compose(
    R.map(({ value, propertyName }) => R.prop('filter', G.createReportFilterFromTitleSearch({
      value,
      fieldProps: {
        type: 'string',
        value: propertyName,
      },
    }))),
    R.reject(({ value }) => G.isNilOrEmpty(value)),
  )(locationFields);
};

const getDriverAdditionalLocationSearchCriteria = (
  values,
  cityPropertyName,
  statePropertyName,
) => {
  const propertyNames = {
    [GC.GRC.FIRST_EVENT_LOCATION_CITY]: cityPropertyName,
    [GC.GRC.FIRST_EVENT_LOCATION_STATE]: statePropertyName,
  };

  const filters = R.compose(
    R.map((filter) => R.assoc(
      GC.FIELD_PROPERTY_NAME,
      R.prop(R.prop(GC.FIELD_PROPERTY_NAME, filter), propertyNames),
      filter,
    )),
    R.filter(({ propertyName }) => R.has(propertyName, propertyNames)),
    R.pathOr([], ['filters']),
  )(R.pathOr(C.defaultAdditionalFiltersAndSort, [C.ADDITIONAL_FILTERS_AND_SORT], values));

  return transformSearchCriteriaBeforeReportPost(filters, GC.ROUTE_TEL_REPORT);
};

export const getDriverRadiusRequestParams = (values = {}) => {
  const locationValue = R.prop(GC.FIELD_ORIGIN_LOCATION, values);
  const radius = R.prop(GC.FIELD_ORIGIN_RADIUS, values);
  const radiusUom = R.prop(GC.FIELD_ORIGIN_RADIUS_UOM, values);
  const latitude = R.prop(GC.FIELD_LATITUDE, locationValue);
  const longitude = R.prop(GC.FIELD_LONGITUDE, locationValue);

  if (G.isAnyNilOrEmpty([radius, radiusUom, latitude, longitude])) return {};

  return {
    radius,
    radiusUom,
    location: {
      latitude,
      longitude,
    },
  };
};

export const getSearchCriteriaForDriversFromFilters = (values = {}) => {
  const searchBy = R.prop(C.SEARCH_BY, values);

  const isFutureAvailabilityLocation = R.equals(
    searchBy,
    GC.FIELD_FUTURE_AVAILABILITY_LOCATION,
  );

  const locationCityPropertyName = isFutureAvailabilityLocation
    ? C.DRIVER_FUTURE_AVAILABILITY_LOCATION_CITY
    : C.DRIVER_LAST_KNOWN_LOCATION_CITY;

  const locationStatePropertyName = isFutureAvailabilityLocation
    ? C.DRIVER_FUTURE_AVAILABILITY_LOCATION_STATE
    : C.DRIVER_LAST_KNOWN_LOCATION_STATE;

  const datePropertyName = isFutureAvailabilityLocation
    ? C.DRIVER_FUTURE_AVAILABILITY_LOCATION_DATE
    : C.DRIVER_LAST_KNOWN_LOCATION_DATE;

  const originZonePropertyName = isFutureAvailabilityLocation
    ? GC.FUTURE_AVAILABILITY_LOCATION_GEOFENCING_ZONE
    : GC.LAST_KNOWN_LOCATION_GEOFENCING_ZONE;

  const originZone = R.prop(GC.FIELD_ORIGIN_ZONE, values);

  const originZoneSearchCriteria = G.isNilOrEmpty(originZone)
    ? []
    : G.getSearchCriteriaByGuidAndPropertyName(originZone, originZonePropertyName);

  const driverBranch = R.prop(C.DRIVER_ENTERPRISE_GUID, values);

  const driverBranchSearchCriteria = G.isNilOrEmpty(driverBranch)
    ? []
    : G.getSearchCriteriaByGuidAndPropertyName(driverBranch, C.DRIVER_ENTERPRISE_GUID);

  const originLocation = R.prop(GC.FIELD_ORIGIN_LOCATION, values);
  const radiusRequestParams = getDriverRadiusRequestParams(values);

  const shouldUseRadiusSearch = R.and(
    R.not(isFutureAvailabilityLocation),
    G.isNotNilAndNotEmpty(radiusRequestParams),
  );

  const locationSearchCriteria = shouldUseRadiusSearch
    ? []
    : getDriverLocationSearchCriteria(
      originLocation,
      locationCityPropertyName,
      locationStatePropertyName,
    );

  const additionalLocationSearchCriteria = getDriverAdditionalLocationSearchCriteria(
    values,
    locationCityPropertyName,
    locationStatePropertyName,
  );

  const dateSearchCriteria = getDateRangeSearchCriteria(
    R.prop(C.ORIGIN_DATE_FROM, values),
    R.prop(C.ORIGIN_DATE_TO, values),
    datePropertyName,
  );

  return R.unnest([
    originZoneSearchCriteria,
    driverBranchSearchCriteria,
    locationSearchCriteria,
    additionalLocationSearchCriteria,
    dateSearchCriteria,
  ]);
};

export const getSearchCriteriaForTripsFromFilters = (values = {}) => {
  const mode = R.prop(C.MODE, values);

  const modeSearchCriteria = G.isNilOrEmpty(mode)
    ? []
    : G.getSearchCriteriaByGuidAndPropertyName(
      mode,
      GC.GRC.RATE_MODE_DROPDOWN_OPTION_GUID,
    );

  const enterpriseGuid = R.path([GC.FIELD_BRANCH, GC.FIELD_GUID], values);

  const enterpriseSearchCriteria = G.isNilOrEmpty(enterpriseGuid)
    ? []
    : G.getSearchCriteriaByGuidAndPropertyName(
      enterpriseGuid,
      GC.FIELD_BRANCH_DOT_GUID,
    );

  const originDateRange = getDateRangeSearchCriteria(
    R.prop(C.ORIGIN_DATE_FROM, values),
    R.prop(C.ORIGIN_DATE_TO, values),
    GC.GRC.FIRST_EVENT_EARLY_DATE,
  );

  const destinationDateRange = getDateRangeSearchCriteria(
    R.prop(C.DESTINATION_DATE_FROM, values),
    R.prop(C.DESTINATION_DATE_TO, values),
    GC.GRC.LAST_EVENT_EARLY_DATE,
  );

  const eventCountRange = getNumberRangeSearchCriteria(
    R.prop(C.EVENTS_MIN, values),
    R.prop(C.EVENTS_MAX, values),
    GC.FIELD_EVENT_COUNT,
  );

  const originZone = R.prop(GC.FIELD_ORIGIN_ZONE, values);

  const originZoneSearchCriteria = G.isNilOrEmpty(originZone)
    ? []
    : G.getSearchCriteriaByGuidAndPropertyName(originZone, GC.FIELD_ORIGIN_ZONE);

  const destinationZone = R.prop(GC.FIELD_DESTINATION_ZONE, values);

  const destinationZoneSearchCriteria = G.isNilOrEmpty(destinationZone)
    ? []
    : G.getSearchCriteriaByGuidAndPropertyName(
      destinationZone,
      GC.FIELD_DESTINATION_ZONE,
    );

  const originRadiusSelected = G.isAllNotNilOrNotEmpty([
    R.prop(GC.FIELD_ORIGIN_RADIUS, values),
    R.prop(GC.FIELD_ORIGIN_RADIUS_UOM, values),
  ]);

  const destinationRadiusSelected = G.isAllNotNilOrNotEmpty([
    R.prop(GC.FIELD_DESTINATION_RADIUS, values),
    R.prop(GC.FIELD_DESTINATION_RADIUS_UOM, values),
  ]);

  const originLocationSearchCriteria = originRadiusSelected
    ? []
    : getOriginLocationSearchCriteria(R.prop(GC.FIELD_ORIGIN_LOCATION, values));

  const destinationLocationSearchCriteria = destinationRadiusSelected
    ? []
    : getDestinationLocationSearchCriteria(R.prop(GC.FIELD_DESTINATION_LOCATION, values));

  const originRadiusSearchCriteria = getLocationRadiusSearchCriteria(
    R.prop(GC.FIELD_ORIGIN_LOCATION, values),
    R.prop(GC.FIELD_ORIGIN_RADIUS, values),
    R.prop(GC.FIELD_ORIGIN_RADIUS_UOM, values),
    GC.GRC.FIRST_EVENT_LOCATION,
  );

  const destinationRadiusSearchCriteria = getLocationRadiusSearchCriteria(
    R.prop(GC.FIELD_DESTINATION_LOCATION, values),
    R.prop(GC.FIELD_DESTINATION_RADIUS, values),
    R.prop(GC.FIELD_DESTINATION_RADIUS_UOM, values),
    GC.GRC.LAST_EVENT_LOCATION,
  );

  const additionalFilters = transformSearchCriteriaBeforeReportPost(
    R.pathOr([], [C.ADDITIONAL_FILTERS_AND_SORT, 'filters'], values),
    GC.ROUTE_TEL_REPORT,
  );

  return R.unnest([
    modeSearchCriteria,
    enterpriseSearchCriteria,
    originDateRange,
    destinationDateRange,
    eventCountRange,
    originZoneSearchCriteria,
    destinationZoneSearchCriteria,
    originLocationSearchCriteria,
    originRadiusSearchCriteria,
    destinationLocationSearchCriteria,
    destinationRadiusSearchCriteria,
    additionalFilters,
  ]);
};

const getLocationDistanceOrderField = (
  location,
  radius,
  radiusUom,
  name,
) => {
  if (G.isAnyNilOrEmpty([location, radius, radiusUom])) return [];

  return [{
    name,
    sequence: 0,
    nullable: true,
    reference: false,
    order: GC.SORT_TYPE_ASC,
    distanceFrom: {
      latitude: R.prop(GC.FIELD_LATITUDE, location),
      longitude: R.prop(GC.FIELD_LONGITUDE, location),
    },
  }];
};

const sequenceOrderFields = (orderFields) => R.last(R.mapAccum(
  (sequence, field) => {
    if (R.equals(R.prop('sequence', field), 0)) return [sequence, field];

    const nextSequence = R.inc(sequence);

    return [nextSequence, R.assoc('sequence', nextSequence, field)];
  },
  0,
  orderFields,
));

export const getOrderFieldsForTripsFromFilters = (values = {}) => {
  const configuredOrderFields = R.compose(
    G.mapIndexed((field, index) => R.assoc('sequence', R.inc(index), field)),
    R.filter((field) => G.isNotNilAndNotEmpty(R.prop(GC.FIELD_NAME, field))),
    R.pathOr([], [C.ADDITIONAL_FILTERS_AND_SORT, 'sort']),
  )(values);

  const originDistanceOrderField = getLocationDistanceOrderField(
    R.prop(GC.FIELD_ORIGIN_LOCATION, values),
    R.prop(GC.FIELD_ORIGIN_RADIUS, values),
    R.prop(GC.FIELD_ORIGIN_RADIUS_UOM, values),
    GC.GRC.FIRST_EVENT_LOCATION,
  );

  return R.unnest([
    originDistanceOrderField,
    configuredOrderFields,
  ]);
};

export const mergeOrderFields = (...orderFields) => R.compose(
  sequenceOrderFields,
  R.uniqBy(R.prop(GC.FIELD_NAME)),
  R.sortBy((field) => (R.equals(R.prop('sequence', field), 0) ? 0 : 1)),
  R.unnest,
)(orderFields);
